import * as React from 'react';
import { useState } from 'react';
import { TTaskComment } from '../../types/Tasks';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import DOMPurify from 'dompurify';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import DeleteIcon from '@mui/icons-material/Delete';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Slide from '@mui/material/Slide';
import { TransitionProps } from '@mui/material/transitions';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';

const SlideTransition = React.forwardRef(function SlideTransition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface ITaskCommentsSectionProps {
  comments: TTaskComment[];
  onAddComment: (comment: string) => void;
  onDeleteComment: (commentId: number) => void;
  unreadCommentCount?: number;
  readOnly?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    commentItem: {
      display: 'flex',
      alignItems: 'flex-start',
      marginBottom: theme.spacing(1.5),
      padding: theme.spacing(1),
      borderRadius: '8px',
      backgroundColor: '#f5f5f5',
    },
    commentIcon: {
      marginRight: theme.spacing(1),
      marginTop: theme.spacing(0.5),
      color: theme.palette.primary.main,
    },
    commentContent: {
      flex: 1,
    },
    commentAuthor: {
      fontWeight: 600,
      fontSize: '0.85rem',
    },
    commentDate: {
      color: theme.palette.text.secondary,
      fontSize: '0.75rem',
    },
    commentText: {
      fontSize: '0.9rem',
      marginTop: theme.spacing(0.5),
    },
    commentForm: {
      marginTop: theme.spacing(1),
    },
    sendButton: {
      backgroundColor: '#4caf50',
      color: '#fff',
      borderRadius: '20px',
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1.25),
      float: 'right' as const,
      '&:hover': {
        backgroundColor: '#388e3c',
      },
    },
    heading: {
      fontWeight: 600,
    },
    unreadBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 20,
      height: 20,
      borderRadius: 10,
      backgroundColor: '#4caf50',
      color: '#fff',
      fontSize: '0.75rem',
      fontWeight: 600,
      padding: '0 6px',
      marginLeft: theme.spacing(1),
    },
  })
);

const TaskCommentsSection: React.FC<ITaskCommentsSectionProps> = ({
  comments,
  onAddComment,
  onDeleteComment,
  unreadCommentCount = 0,
  readOnly = false,
}): JSX.Element => {
  const classes = useStyles({});
  const { t } = useTranslation();
  const [commentText, setCommentText] = useState('');
  const [deleteCommentId, setDeleteCommentId] = useState<number | null>(null);

  const handleConfirmDelete = () => {
    if (deleteCommentId !== null) {
      onDeleteComment(deleteCommentId);
      setDeleteCommentId(null);
    }
  };

  const handleSubmit = () => {
    if (commentText.trim()) {
      onAddComment(commentText.trim());
      setCommentText('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Accordion defaultExpanded={unreadCommentCount > 0}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography className={classes.heading}>
          {t('tasks.detail.comments')}
          {unreadCommentCount > 0 && (
            <span className={classes.unreadBadge}>{unreadCommentCount}</span>
          )}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box width="100%">
          {comments.map((comment) => (
            <Box key={comment.id} className={classes.commentItem}>
              <AccountCircleIcon className={classes.commentIcon} />
              <Box className={classes.commentContent}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography className={classes.commentAuthor}>
                      {comment.authorName}
                    </Typography>
                    <Typography className={classes.commentDate}>
                      {comment.createdAt}
                    </Typography>
                  </Box>
                  {comment.isOwnComment && !readOnly && (
                    <IconButton
                      size="small"
                      onClick={() => setDeleteCommentId(comment.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                <Typography
                  className={classes.commentText}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(comment.comment),
                  }}
                />
              </Box>
            </Box>
          ))}
          {!readOnly && (
            <Box className={classes.commentForm}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder={t('tasks.detail.add_comment')}
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onKeyPress={handleKeyPress}
                multiline
                rows={4}
              />
              <Button
                variant="contained"
                className={classes.sendButton}
                onClick={handleSubmit}
                disabled={!commentText.trim()}
              >
                {t('tasks.detail.send')}
              </Button>
            </Box>
          )}
        </Box>
      </AccordionDetails>
      <Dialog
        open={deleteCommentId !== null}
        TransitionComponent={SlideTransition}
        onClose={() => setDeleteCommentId(null)}
      >
        <DialogTitle>{t('tasks.detail.delete_comment_title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('tasks.detail.delete_warning')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteCommentId(null)} color="primary">
            {t('tasks.detail.disagree')}
          </Button>
          <Button onClick={handleConfirmDelete} color="secondary">
            {t('tasks.detail.agree')}
          </Button>
        </DialogActions>
      </Dialog>
    </Accordion>
  );
};

export default TaskCommentsSection;
